import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

import { ToastLevel } from 'src/app/shared/models/toast-message.module';
import { ToastService } from 'src/app/shared/services/toast.service';

import { DocumentService } from '../../../core/api/document.service';
import { KmlForestryUploadService } from '../../../core/api/kml-forestry-upload.service';
import { SiteService } from '../../../core/api/site.service';
import { User } from '../../../shared/models';
import * as fromApp from '../../../store';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { clearCreateSiteStepThree, deleteForestryKml } from '../../../store/parcel/parcel.actions';
import { selectParcelState } from '../../../store/parcel/parcel.selectors';
import { createSiteFailure, createSiteSuccess } from '../../../store/site/site.actions';
import { selectActiveSite } from '../../../store/site/site.selectors';

@Component({
  selector: 'fc-new-site-step-three',
  templateUrl: './new-site-step-three.component.html',
  styleUrls: ['./new-site-step-three.component.scss'],
})
export class NewSiteStepThreeComponent implements OnInit {
  @ViewChild('kmlFileUpload', { static: false }) kmlFileUpload: ElementRef;

  destroyRef = inject(DestroyRef);

  newSiteForm: UntypedFormGroup;

  kmlUpload = false;
  currentUser: User = null;
  activeSiteId: number = null;
  fileUpload = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    readonly router: Router,
    readonly store: Store<fromApp.AppState>,
    readonly kmlForestryUploadService: KmlForestryUploadService,
    readonly siteService: SiteService,
    readonly documentService: DocumentService,
    readonly toastService: ToastService
  ) {
    this.createNewSiteForm();
  }

  ngOnInit() {
    this.store
      .select(selectParcelState)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(parcelState => {
        if (!parcelState.mapCenter?.lat || !parcelState.mapCenter?.lng) {
          this.router.navigate(['/site/new-site/step-two']);
        }
      });

    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.currentUser = currentUser;
      });

    this.store
      .select(selectActiveSite)
      .pipe(
        filter(site => !isEmpty(site)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(activeSite => {
        this.activeSiteId = activeSite ? activeSite.id : null;
      });

    this.store.dispatch(clearCreateSiteStepThree());
    window.scrollTo(0, 100);
  }

  createNewSiteForm(): void {
    this.newSiteForm = this.formBuilder.group({
      locationType: 'geoLocate',
      uploadKmlFileName: '',
    });
  }

  radioChange(event: MatRadioChange): void {
    switch (event.value) {
      case 'geoLocate':
        this.kmlUpload = false;
        this.store.dispatch(deleteForestryKml());
        this.uploadKmlFileNameControl.clearValidators();
        this.uploadKmlFileNameControl.setValue(null);
        break;
      case 'kml':
        this.uploadKmlFileNameControl.setValidators(Validators.required);
        this.uploadKmlFileNameControl.updateValueAndValidity();
        this.kmlUpload = true;
        break;
      default:
    }
  }

  onClickKmlUpload(): void {
    const kmlFileUpload = this.kmlFileUpload.nativeElement;
    kmlFileUpload.onchange = () => {
      if (kmlFileUpload.files.length === 1) {
        this.fileUpload = kmlFileUpload.files[0];
        this.uploadKmlFile(this.fileUpload);
      }
    };
    kmlFileUpload.click();
  }

  uploadKmlFile = (file: any) => {
    const fileExtension = file.name.substr(file.name.lastIndexOf('.') + 1);
    switch (fileExtension) {
      case 'kml':
        this.kmlForestryUploadService
          .parseKmlDocument(file)
          .then(parsedKml => {
            this.uploadKmlFileNameControl.setValue(this.fileUpload.name);
          })
          .catch(error => {
            this.fileUpload = null;
            this.uploadKmlFileNameControl.setValue(null);
          });
        break;
      case 'kmz':
        this.kmlForestryUploadService
          .parseKmzDocument(file)
          .then(parsedKml => {
            this.uploadKmlFileNameControl.setValue(this.fileUpload.name);
          })
          .catch(error => {
            this.fileUpload = null;
            this.uploadKmlFileNameControl.setValue(null);
          });
        break;
      default:
        this.toastService.sendMessage('Only .kml and .kmz files are supported.', ToastLevel.WARN);
        this.fileUpload = null;
        this.uploadKmlFileNameControl.setValue(null);
    }
  };

  goNext(): void {
    this.siteService
      .createSite()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(
        site => {
          if (this.kmlUpload) {
            this.kmlForestryUploadService.uploadKMLToSite(site, this.fileUpload);
          }
          this.store.dispatch(createSiteSuccess({ data: site }));
        },
        error => this.store.dispatch(createSiteFailure({ error }))
      );
  }

  goBack() {
    this.router.navigate(['/site/new-site/step-two']);
  }

  get uploadKmlFileName(): AbstractControl {
    return this.newSiteForm.get('uploadKmlFileName').value;
  }

  get uploadKmlFileNameControl(): AbstractControl {
    return this.newSiteForm.get('uploadKmlFileName');
  }
}
